import Papa from "papaparse";
import { autoDetect, getMapping, normalizeRow } from "./mapper";

/*
  parser.js — CSV file parser (filtered)

  Key design: parsing is filtered at the row level.
  Instead of loading all records then filtering, we filter DURING parsing
  so only matching vessel records ever touch memory.

  This is critical for large NOAA files (500MB+) — without this,
  storing millions of records would exhaust browser memory.

  vesselTargets: array of { mmsi?, vesselName? } objects
  If empty or null, all records are kept (useful for small test files)
*/

/*
  rowMatchesTargets — returns true if a normalized row matches any target vessel.
  Matches by MMSI (exact) or vessel name (case-insensitive partial match).
*/
function rowMatchesTargets(row, targets) {
  if (!targets || targets.length === 0) return true;

  return targets.some((target) => {
    if (target.mmsi && row.mmsi) {
      if (row.mmsi.toString() === target.mmsi.toString()) return true;
    }
    if (target.vesselName && row.vesselName) {
      if (row.vesselName.toLowerCase().includes(target.vesselName.toLowerCase())) return true;
    }
    return false;
  });
}

/*
  parseFile — parses a single CSV file, keeping only matching vessel records.

  file: File object from the browser
  vesselTargets: array of { mmsi?, vesselName? } — which vessels to keep
  onProgress: optional callback (percent: 0-100, recordCount: number)
*/
export function parseFile(file, vesselTargets, onProgress) {
  return new Promise((resolve, reject) => {
    let records = [];
    let mapping = null;
    let bytesProcessed = 0;
    const totalBytes = file.size;

    Papa.parse(file, {
      chunk: (results) => {
        // Detect the data provider on the first chunk that has data
        if (!mapping && results.data.length > 0) {
          const headers = Object.keys(results.data[0]);
          const provider = autoDetect(headers);
          mapping = getMapping(provider);
        }

        // Skip chunk if mapping still not resolved (empty first chunk edge case)
        if (!mapping) return;

        // Normalize and filter in a single pass
        // Only matching records ever get pushed into memory
        for (const row of results.data) {
          const normalized = normalizeRow(row, mapping);
          if (isNaN(normalized.lat) || isNaN(normalized.lng)) continue;
          if (!rowMatchesTargets(normalized, vesselTargets)) continue;
          records.push(normalized);
        }

        // Track bytes for progress bar
        if (results.meta && results.meta.cursor) {
          bytesProcessed = results.meta.cursor;
        }

        // Cap at 95% — only hits 100% when complete fires
        const percent = totalBytes > 0
          ? Math.min(95, Math.round((bytesProcessed / totalBytes) * 100))
          : 0;

        if (onProgress) onProgress(percent, records.length);
      },

      complete: () => {
        if (onProgress) onProgress(100, records.length);
        resolve(records);
      },

      error: (error) => reject(error),
      header: true,
      skipEmptyLines: true,
    });
  });
}

/*
  parseFiles — parses multiple files in sequence, filtering each one.

  files: array of File objects
  vesselTargets: array of { mmsi?, vesselName? }
  onProgress: callback (fileIndex, totalFiles, filename, percent, recordCount)
*/
export async function parseFiles(files, vesselTargets, onProgress) {
  const results = [];

  for (let i = 0; i < files.length; i++) {
    const file = files[i];

    const records = await parseFile(file, vesselTargets, (percent, recordCount) => {
      if (onProgress) {
        onProgress(i, files.length, file.name, percent, recordCount);
      }
    });

    results.push(records);
  }

  return results;
}
